import React, { ChangeEvent, useState } from 'react'
import CustomInput from '../components/CustomInput'
import CustomButton from '../components/CustomButton'
import ModalPortal from '../components/modal-portal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { ParseErrors } from '../utils/ParseErrors'
import axiosInstance from '../utils/axiosInstance'
import Loader from '../components/Loader'
import { useProviderEditModel } from '../store/use-provider-edit-model'
import CustomRadioButton from '../components/CustomRadioButton'
import { useDebounceCallback } from 'usehooks-ts'

interface IProviderChaseModel {
    UpdateItemData: (data: Record<string, any>) => void
}

const ProviderChaseModel: React.FC<IProviderChaseModel> = ({ UpdateItemData }) => {
    const { initialValues, onClose } = useProviderEditModel()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [newValues, setNewValues] = useState<Record<string, any>>({
        type: initialValues.data?.type,
        product: initialValues.data?.product,
        productType: initialValues.data?.productType
    })

    // --------------------------------- [ UPDATE PROVIDER ] --------------------------------
    const updateProvider = async () => {
        setIsLoading(true)
        try {
            const res = await axiosInstance.patch(`/provider/${initialValues.id}`, newValues)
            toast.success(res.data.message)
            UpdateItemData(newValues)
            setNewValues({})
            setIsLoading(false)
            onClose()
        } catch (error) {
            console.log(error)
            toast.error(ParseErrors(error))
            setIsLoading(false)
        }
    }

    // ------------------------------ [ ON CHANGE VALUES ] --------------------------------
    const onChangeValues = useDebounceCallback((e: ChangeEvent<HTMLInputElement>) => {
        setNewValues((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    }, 200)

    return (
        <ModalPortal>
        <div className="z-[99999] fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto py-6 sm:py-10 px-3 sm:px-5 animate-fade-in" onClick={onClose}>
            <div className="bg-surface border border-edge w-full max-w-[500px] smallTablet:max-w-[calc(100vw-2rem)] rounded-2xl shadow-overlay" onClick={(e) => e.stopPropagation()}>
                {/*---------------------------- [ HEADER ] ------------------------------- */}
                <div className="flex items-center justify-between py-4 px-6 smallTablet:px-4 border-b border-edge-faint">
                    <h2 className="font-heading-font text-[22px] sm:text-[25px] bg-gradient-heading bg-clip-text text-transparent">Update Provider</h2>
                    <button
                        onClick={onClose}
                        className="h-9 w-9 flex items-center justify-center rounded-lg border border-edge bg-overlay text-text-primary hover:border-indigo-400/40 transition-all"
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                {/*---------------------------- [ HEADER ] ------------------------------- */}

                {/* -------------------------- [ BODY ] ------------------------------------ */}
                <div className='p-4 sm:p-8'>
                    <CustomInput label='Insurer Name' name='name' defaultValue={newValues?.name || initialValues?.data.name} onChange={onChangeValues} />
                    <div className='my-4'></div>
                    <CustomInput label='Insurer Email' name='email' defaultValue={newValues?.email || initialValues?.data.email} onChange={onChangeValues} />
                    <div className='my-6'></div>
                    <CustomInput label='Insurer Scheme Code' name='schemeCode' defaultValue={newValues?.schemeCode || initialValues?.data.schemeCode} onChange={onChangeValues} />
                    <div className='my-6'></div>
                    <CustomInput label='Product Type' name='productType' defaultValue={newValues?.productType || initialValues?.data.productType} onChange={onChangeValues} />
                    <div className='my-6'></div>
                    <div className="w-full space-y-2">
                        <label className="text-label-muted font-medium block text-sm leading-[16.98px]">
                            Type
                        </label>
                        <div className="flex gap-10 smallTablet:gap-3 smallTablet:flex-wrap">
                            <CustomRadioButton
                                id="newBussiness"
                                name="type"
                                label="Sales"
                                checked={newValues?.type === "Sales"}
                                onChange={onChangeValues}
                                value="Sales"
                            />
                            <CustomRadioButton
                                id="Renewals"
                                name="type"
                                label="Renewals"
                                value="Renewals"
                                checked={newValues?.type === "Renewals"}
                                onChange={onChangeValues}
                            />
                        </div>
                    </div>
                    <div className='my-6'></div>
                    <div className="w-full space-y-2">
                        <label className="text-label-muted font-medium block text-sm leading-[16.98px]">
                            Product
                        </label>
                        <div className="flex gap-10 smallTablet:gap-3 smallTablet:flex-wrap">
                            <CustomRadioButton
                                id="standard"
                                name="product"
                                label="Standard"
                                checked={newValues?.product === "Standard"}
                                onChange={onChangeValues}
                                value="Standard"
                            />
                            <CustomRadioButton
                                id="nonStandard"
                                name="product"
                                label="Non-Standard"
                                value="Non-Standard"
                                checked={newValues?.product === "Non-Standard"}
                                onChange={onChangeValues}
                            />
                        </div>
                    </div>

                    <div className='my-10'></div>

                    <CustomButton disabled={isLoading} fullWidth variant='primary' className='w-full' onClick={updateProvider}>
                        {isLoading ? <Loader /> : "UPDATE"}
                    </CustomButton>
                </div>
                {/* -------------------------- [ BODY ] ------------------------------------ */}
            </div>
        </div>
        </ModalPortal>
    )
}

export default ProviderChaseModel
